import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, AlertCircle, Building2, Hash, ExternalLink } from 'lucide-react';
import { useMasterActionCatalog } from '../../hooks/useProgramDesign';
import DataTable from '../../components/DataTable';

const DESCRIPTION_TRUNCATE_LENGTH = 100;

function formatTimestamp(value) {
  if (!value) return <span className="text-gray-400">-</span>;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const ALL_SEARCHABLE_KEYS = [
  'tenant_code', 'tenant_name', 'task_code', 'task_name', 'task_header',
  'CTA', 'action_category', 'action_category_desc', 'reward_amount',
  'overall_priority', 'valid_start_ts', 'valid_end_ts', 'self_report',
  'is_recurring', 'task_external_code', 'deep_link_url', 'task_description',
];

function InlineReveal({ label, value, icon: Icon, isOpen, onToggle, popoverRef, isLink }) {
  return (
    <div className="relative inline-block ml-1 align-middle">
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={`p-0.5 rounded transition-colors ${isOpen ? 'bg-amber-100' : 'hover:bg-gray-200'}`}
        title={`Show ${label}`}
      >
        <Icon className={`w-3.5 h-3.5 ${isOpen ? 'text-amber-600' : 'text-gray-400'}`} />
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-6 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-xs whitespace-normal min-w-[180px] max-w-[300px]"
        >
          <span className="text-gray-400 font-medium uppercase text-[10px]">{label}</span>
          {isLink && value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-amber-600 hover:text-amber-800 underline break-all mt-0.5"
            >
              {value}
            </a>
          ) : (
            <p className="text-gray-900 break-all mt-0.5">{value || '-'}</p>
          )}
        </div>
      )}
    </div>
  );
}

const PARSE_PLAIN = 'plain';
const PARSE_JSON = 'json';
const PARSE_ERROR = 'error';

function parseDescription(raw) {
  if (!raw) return { type: PARSE_PLAIN, value: '' };
  const str = typeof raw === 'string' ? raw.trim() : String(raw);
  if (!str.startsWith('[')) return { type: PARSE_PLAIN, value: str };
  try {
    const blocks = JSON.parse(str);
    if (!Array.isArray(blocks)) return { type: PARSE_ERROR };
    return { type: PARSE_JSON, value: blocks };
  } catch {
    return { type: PARSE_ERROR };
  }
}

function DescriptionPreview({ value }) {
  const parsed = useMemo(() => parseDescription(value), [value]);

  if (parsed.type === PARSE_ERROR) {
    return <span className="text-red-600 font-medium text-xs">FORMATTING ERROR</span>;
  }

  if (parsed.type === PARSE_PLAIN) {
    const text = parsed.value;
    if (!text) return <span className="text-gray-400">-</span>;
    return (
      <div className="whitespace-normal max-w-[350px] text-xs sm:text-sm">
        {text.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="whitespace-normal max-w-[350px] text-xs sm:text-sm space-y-2">
      {parsed.value.map((block, i) => {
        const text = block?.data?.text || '';
        if (!text) return null;
        if (block.type === 'paragraph' || !block.type) {
          return (
            <div key={i}>
              {text.split('\n').map((line, j) => (
                <React.Fragment key={j}>
                  {j > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </div>
          );
        }
        if (block.type === 'header') {
          return <p key={i} className="font-semibold">{text}</p>;
        }
        return <p key={i}>{text}</p>;
      })}
    </div>
  );
}

function ExpandableDescription({ value, rowIndex, expandedSet, onToggle }) {
  const text = value != null ? String(value) : '';
  if (!text) return <span className="text-gray-400">-</span>;

  const isLong = text.length > DESCRIPTION_TRUNCATE_LENGTH;
  const isExpanded = expandedSet.has(rowIndex);

  if (!isLong) {
    return <div className="whitespace-normal max-w-[400px] text-xs sm:text-sm">{text}</div>;
  }

  return (
    <div className="whitespace-normal max-w-[400px] text-xs sm:text-sm">
      {isExpanded ? text : `${text.slice(0, DESCRIPTION_TRUNCATE_LENGTH)}...`}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(rowIndex); }}
        className="ml-1 text-amber-600 hover:text-amber-800 font-medium"
      >
        {isExpanded ? 'less' : 'more'}
      </button>
    </div>
  );
}

function uniqueValues(data, key) {
  const vals = [...new Set(data.map((r) => r[key]).filter((v) => v != null && v !== ''))];
  return vals.sort((a, b) => String(a).localeCompare(String(b)));
}

export default function MasterActionCatalog() {
  const { data: rows = [], isLoading, isError, error } = useMasterActionCatalog();

  const [globalSearch, setGlobalSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selfReportFilter, setSelfReportFilter] = useState('all');
  const [recurringFilter, setRecurringFilter] = useState('all');
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [activeReveal, setActiveReveal] = useState(null);

  const revealRef = useRef(null);

  useEffect(() => {
    if (!activeReveal) return;

    const handleClickOutside = (e) => {
      if (revealRef.current && !revealRef.current.contains(e.target)) {
        setActiveReveal(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeReveal]);

  const toggleDescription = useCallback((rowIndex) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  }, []);

  const toggleReveal = useCallback((rowIndex, field) => {
    setActiveReveal((prev) =>
      prev?.rowIndex === rowIndex && prev?.field === field ? null : { rowIndex, field }
    );
  }, []);

  const isRevealOpen = useCallback((rowIndex, field) => {
    return activeReveal?.rowIndex === rowIndex && activeReveal?.field === field;
  }, [activeReveal]);

  const tenantOptions = useMemo(() => uniqueValues(rows, 'tenant_name'), [rows]);
  const categoryOptions = useMemo(() => uniqueValues(rows, 'action_category'), [rows]);
  const selfReportOptions = useMemo(() => uniqueValues(rows, 'self_report'), [rows]);
  const recurringOptions = useMemo(() => uniqueValues(rows, 'is_recurring'), [rows]);

  const filteredData = useMemo(() => {
    let result = rows;

    if (tenantFilter !== 'all') {
      result = result.filter((r) => r.tenant_name === tenantFilter);
    }
    if (categoryFilter !== 'all') {
      result = result.filter((r) => r.action_category === categoryFilter);
    }
    if (selfReportFilter !== 'all') {
      result = result.filter((r) => String(r.self_report) === selfReportFilter);
    }
    if (recurringFilter !== 'all') {
      result = result.filter((r) => String(r.is_recurring) === recurringFilter);
    }
    if (globalSearch.trim()) {
      const term = globalSearch.toLowerCase();
      result = result.filter((r) =>
        ALL_SEARCHABLE_KEYS.some((key) => {
          const val = r[key];
          return val != null && String(val).toLowerCase().includes(term);
        })
      );
    }

    return result;
  }, [rows, tenantFilter, categoryFilter, selfReportFilter, recurringFilter, globalSearch]);

  const columns = useMemo(() => [
    {
      key: 'tenant_name',
      label: 'Tenant',
      sortable: true,
      render: (value, row) => {
        const idx = filteredData.indexOf(row);
        const open = isRevealOpen(idx, 'tenant_code');
        return (
          <span className="inline-flex items-center">
            {value || '-'}
            <InlineReveal
              label="Tenant Code"
              value={row.tenant_code}
              icon={Building2}
              isOpen={open}
              onToggle={() => toggleReveal(idx, 'tenant_code')}
              popoverRef={open ? revealRef : undefined}
            />
          </span>
        );
      },
    },
    {
      key: 'task_name',
      label: 'Task Name',
      sortable: true,
      render: (value, row) => {
        const idx = filteredData.indexOf(row);
        const open = isRevealOpen(idx, 'task_code');
        return (
          <span className="inline-flex items-center">
            {value || '-'}
            <InlineReveal
              label="Task Code"
              value={row.task_code}
              icon={Hash}
              isOpen={open}
              onToggle={() => toggleReveal(idx, 'task_code')}
              popoverRef={open ? revealRef : undefined}
            />
          </span>
        );
      },
    },
    { key: 'task_header', label: 'Task Header', sortable: true },
    { key: 'CTA', label: 'CTA', sortable: true },
    { key: 'action_category', label: 'Category', sortable: true },
    { key: 'action_category_desc', label: 'Category Desc', sortable: true },
    { key: 'reward_amount', label: 'Reward', sortable: true, format: 'number' },
    { key: 'overall_priority', label: 'Priority', sortable: true, format: 'number' },
    { key: 'valid_start_ts', label: 'Valid Start', sortable: true, render: (v) => formatTimestamp(v) },
    { key: 'valid_end_ts', label: 'Valid End', sortable: true, render: (v) => formatTimestamp(v) },
    { key: 'self_report', label: 'Self Report', sortable: true },
    { key: 'is_recurring', label: 'Recurring', sortable: true },
    { key: 'task_external_code', label: 'External Code', sortable: true },
    {
      key: 'task_description',
      label: 'Description',
      sortable: true,
      render: (value, row) => {
        const idx = filteredData.indexOf(row);
        const open = isRevealOpen(idx, 'deep_link');
        return (
          <div className="inline-flex items-start gap-1">
            <ExpandableDescription
              value={value}
              rowIndex={idx}
              expandedSet={expandedDescriptions}
              onToggle={toggleDescription}
            />
            <InlineReveal
              label="Deep Link"
              value={row.deep_link_url}
              icon={ExternalLink}
              isOpen={open}
              onToggle={() => toggleReveal(idx, 'deep_link')}
              popoverRef={open ? revealRef : undefined}
              isLink
            />
          </div>
        );
      },
    },
    {
      key: '_preview',
      label: 'Preview',
      sortable: false,
      render: (_value, row) => <DescriptionPreview value={row.task_description} />,
    },
  ], [filteredData, activeReveal, expandedDescriptions, toggleDescription, toggleReveal, isRevealOpen]);

  const hasActiveFilters =
    globalSearch || tenantFilter !== 'all' || categoryFilter !== 'all' ||
    selfReportFilter !== 'all' || recurringFilter !== 'all';

  const clearFilters = () => {
    setGlobalSearch('');
    setTenantFilter('all');
    setCategoryFilter('all');
    setSelfReportFilter('all');
    setRecurringFilter('all');
  };

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Master Action Catalog</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800">Failed to load catalog</p>
            <p className="text-sm text-red-600 mt-1">{error?.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Master Action Catalog</h1>
        <span className="text-sm text-gray-500">
          {isLoading ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </span>
          ) : (
            `${filteredData.length.toLocaleString()} of ${rows.length.toLocaleString()} records`
          )}
        </span>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search all columns..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
          >
            <option value="all">All Tenants</option>
            {tenantOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <select
            value={selfReportFilter}
            onChange={(e) => setSelfReportFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
          >
            <option value="all">Self Report</option>
            {selfReportOptions.map((v) => (
              <option key={String(v)} value={String(v)}>{String(v)}</option>
            ))}
          </select>

          <select
            value={recurringFilter}
            onChange={(e) => setRecurringFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
          >
            <option value="all">Recurring</option>
            {recurringOptions.map((v) => (
              <option key={String(v)} value={String(v)}>{String(v)}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      <DataTable
        data={filteredData}
        columns={columns}
        loading={isLoading}
        emptyMessage="No catalog entries match the current filters"
      />
    </div>
  );
}
